import type { ElementType } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Stack,
  CircularProgress,
  Box,
  styled,
} from '@mui/material';
import { theme } from '~/shared/styles/theme';
import { ROUTES } from '~/shared/constants';
import {
  RadioButtonGroup,
  PrimaryButton,
  OnboardingLayout,
} from '../components';
import { handleLogout as handleLogoutUtil } from '~/shared/utils/auth';
import { CALCULATE_RISK_SCORE } from '~/graphql/mutations';
import { GET_ONBOARDING_STATUS } from '~/graphql/queries';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuestionnaireStore } from '../stores/questionnaireStore';
import { ANSWER_LABELS, AnswerValue, QuestionConfig } from '../constants';
import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import type {
  CalculateRiskScoreMutation,
  CalculateRiskScoreMutationVariables,
} from '~/graphql/gen/graphql';
import useToast from '~/shared/hooks/useToast';
import Toast from '~/shared/components/Toast';

interface QuestionFormData {
  answer: AnswerValue | '';
}

interface Props {
  currentQuestion: QuestionConfig;
  selectedAnswer: AnswerValue | '';
  isLoading: boolean;
  onSubmit: (data: QuestionFormData) => Promise<void>;
}

const QuestionForm = ({
  currentQuestion,
  selectedAnswer,
  isLoading,
  onSubmit,
}: Props) => {
  const { control, handleSubmit, watch } = useForm<QuestionFormData>({
    defaultValues: { answer: selectedAnswer },
  });

  const answer = watch('answer');

  const answerOptions = currentQuestion.answerOptions.map((answerOption) => ({
    value: answerOption,
    label: ANSWER_LABELS[answerOption],
  }));

  return (
    <FormStack component="form" onSubmit={handleSubmit(onSubmit)}>
      <RadioButtonGroup
        name="answer"
        control={control}
        options={answerOptions}
      />

      <Box sx={{ flex: 1, minHeight: theme.spacing(10.5) }} />

      <Stack alignItems="center">
        <PrimaryButton
          type="submit"
          variant="green"
          disabled={!answer || isLoading}
        >
          Next
        </PrimaryButton>
      </Stack>
    </FormStack>
  );
};

export const QuestionScreen = () => {
  const navigate = useNavigate();
  const { logout } = useAuth0();

  const {
    currentStep,
    responses,
    getCurrentQuestion,
    getProgressPercentage,
    canGoBack,
    goToNextStep,
    goToPreviousStep,
    setAnswer,
    getAllResponses,
    resetQuestionnaire,
    getVisibleQuestions,
  } = useQuestionnaireStore();

  const currentQuestion = getCurrentQuestion();
  const progressPercentage = getProgressPercentage();
  const selectedAnswer = currentQuestion ? responses[currentQuestion.key] : '';

  const visibleQuestions = getVisibleQuestions();
  const isLastQuestion = currentStep === visibleQuestions.length - 1;

  const { toast, showToast, hideToast } = useToast();

  const [calculateRiskScore, { loading: submitLoading }] = useMutation<
    CalculateRiskScoreMutation,
    CalculateRiskScoreMutationVariables
  >(CALCULATE_RISK_SCORE, {
    refetchQueries: [{ query: GET_ONBOARDING_STATUS }],
    awaitRefetchQueries: true,
  });

  const onSubmit = async (data: QuestionFormData) => {
    if (!currentQuestion || !data.answer) {
      return;
    }

    try {
      setAnswer(currentQuestion.key, data.answer);

      if (isLastQuestion) {
        // On final question, call mutation with all responses
        // Note: setAnswer was already called above, so getAllResponses()
        // includes the current answer
        const allResponses = getAllResponses();

        await calculateRiskScore({
          variables: {
            responses: allResponses,
          },
        });

        resetQuestionnaire();
        navigate(ROUTES.ONBOARDING.RESULTS);
      } else {
        goToNextStep();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';

      showToast(
        `Unable to calculate your risk score. ${errorMessage}`,
        'error',
      );
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      navigate(ROUTES.ONBOARDING.CONSENT);
    } else {
      goToPreviousStep();
    }
  };

  const handleLogOut = () => {
    handleLogoutUtil(logout);
  };

  if (!currentQuestion) {
    return (
      <OnboardingLayout
        showProgress={true}
        progressPercentage={progressPercentage}
      >
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      showProgress={true}
      progressPercentage={progressPercentage}
      showBack={canGoBack()}
      onBack={handleBack}
      showLogOut={true}
      onLogOut={handleLogOut}
    >
      <ContentContainer>
        <QuestionText>{currentQuestion.questionText}</QuestionText>

        <QuestionForm
          key={currentStep}
          currentQuestion={currentQuestion}
          selectedAnswer={selectedAnswer ?? ''}
          isLoading={submitLoading}
          onSubmit={onSubmit}
        />
      </ContentContainer>

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={hideToast}
      />
    </OnboardingLayout>
  );
};

const FormStack = styled(Stack)<{ component?: ElementType }>({
  flex: 1,
});

const ContentContainer = styled(Stack)({
  flex: 1,
  paddingTop: theme.spacing(7),
  backgroundColor: theme.palette.custom.offWhite,
});

const QuestionText = styled(Typography)({
  fontSize: '30px',
  fontWeight: 500,
  color: theme.palette.custom.darkBrown,
  textAlign: 'center',
  lineHeight: 1.2,
  paddingBottom: theme.spacing(14),
});
